/**
 * Node Type Helpers
 * Utilities for working with LoadSEER node types
 * Uses API-provided node type data instead of hard-coded mappings
 */

import type { NodeTypeDto } from '../types';

/**
 * Generates a deterministic color for a node type code
 * Uses hash-based color generation for consistency
 */
export function getNodeTypeColor(nodeType: string): string {
  // Generate a hash from the node type string
  let hash = 0;
  for (let i = 0; i < nodeType.length; i++) {
    hash = nodeType.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert hash to HSL color with good contrast
  const hue = Math.abs(hash % 360);
  const saturation = 65; // Vibrant but not oversaturated
  const lightness = 50; // Medium brightness for good contrast

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

/**
 * Gets display name from node type mapping or falls back to code
 */
export function getNodeTypeName(nodeType: string, nodeTypes?: NodeTypeDto[]): string {
  if (!nodeTypes) {
    return nodeType;
  }

  const match = nodeTypes.find(nt => nt.code === nodeType);
  return match?.name || nodeType;
}

/**
 * Gets description from node type mapping
 */
export function getNodeTypeDescription(nodeType: string, nodeTypes?: NodeTypeDto[]): string | null {
  if (!nodeTypes) {
    return null;
  }

  const match = nodeTypes.find(nt => nt.code === nodeType);
  return match?.description || null;
}

/**
 * Filters node types to only include grid asset types
 * Grid asset types have the "grid asset" or "grid-asset" label in their defaultLabels array
 * Uses case-insensitive matching for flexibility
 */
export function filterGridAssetNodeTypes(nodeTypes: NodeTypeDto[]): NodeTypeDto[] {
  return nodeTypes.filter(nt =>
    nt.defaultLabels.some(label => {
      const normalized = label.toLowerCase().replace(/[\s-_]/g, '');
      return normalized === 'gridasset';
    })
  );
}

/**
 * Extracts the NT_ prefixed label from a node type's defaultLabels
 * This label is used for hierarchy traversal with stopAtLabel parameter
 *
 * @param nodeType - The node type to extract NT_ label from
 * @returns The NT_ label or null if none found
 *
 * @example
 * // NodeType with defaultLabels: ["grid asset", "NT_SUBSTATION", "electrical"]
 * extractNTLabel(nodeType) // Returns "NT_SUBSTATION"
 */
export function extractNTLabel(nodeType: NodeTypeDto): string | null {
  const ntLabel = nodeType.defaultLabels.find(label => label.toUpperCase().startsWith('NT_'));
  return ntLabel || null;
}

/**
 * Formats a node type code as a badge label
 */
export function formatNodeTypeBadge(nodeType: string): string {
  return `[${nodeType}]`;
}
